import { makeStyles } from '@material-ui/core/styles';
import { Typography, Slider, Box, Button } from '@material-ui/core';
import MyLocationIcon from '@material-ui/icons/MyLocation';
import OpenWithIcon from '@material-ui/icons/OpenWith';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

const useStyles = makeStyles(() => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  left: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  title: {
    fontSize: 14,
    fontWeight: 500,
  },
  value: {
    fontSize: 14,
    fontWeight: 500,
    color: '#1565c0',
  },
  slider: {
    color: '#1565c0',
  },
  range: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 4,
    fontSize: 11,
    color: '#999',
  },
  hint: {
    fontSize: 11,
    color: '#999',
    marginTop: 8,
    display: 'block',
  },
  repositionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 12,
  },
  repositionBtn: {
    textTransform: 'none',
    fontSize: 12,
    padding: '4px 10px',
  },
  customChip: {
    fontSize: 10,
    padding: '2px 6px',
    background: '#EEF2FF',
    color: '#4F46E5',
    borderRadius: 4,
    marginLeft: 6,
  },
  confirmRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginTop: 12,
    padding: '8px 10px',
    background: '#F8FAFC',
    border: '1px solid #E2E8F0',
    borderRadius: 6,
  },
  confirmRowConfirmed: {
    background: '#ECFDF5',
    border: '1px solid #A7F3D0',
  },
  confirmText: {
    fontSize: 12,
    color: '#374151',
    flex: 1,
  },
  confirmTextDone: {
    color: '#065F46',
    fontWeight: 500,
  },
  confirmBtn: {
    textTransform: 'none',
    fontSize: 12,
    padding: '4px 12px',
  },
}));

const GeofenceSection = ({
  radius,
  onRadiusChange,
  onRadiusCommit,
  disabled,
  repositioning,
  hasCustomCenter,
  onStartReposition,
  onConfirmReposition,
  onCancelReposition,
  onResetCenter,
  confirmed,
  onConfirm,
  saving,
}) => {
  const classes = useStyles();
  const radiusFt = Math.round((radius || 200) * 3.28084);

  return (
    <Box>
      <div className={classes.header}>
        <div className={classes.left}>
          <MyLocationIcon style={{ fontSize: 18, color: '#666' }} />
          <Typography className={classes.title}>Geofence radius</Typography>
          {hasCustomCenter && <span className={classes.customChip}>Repositioned</span>}
        </div>
        <span className={classes.value}>{radiusFt} ft</span>
      </div>
      <Slider
        value={radius || 200}
        onChange={(_, val) => onRadiusChange(val)}
        onChangeCommitted={(_, val) => onRadiusCommit(val)}
        min={15}
        max={1500}
        step={5}
        className={classes.slider}
        disabled={disabled || repositioning}
        valueLabelDisplay="auto"
        valueLabelFormat={(val) => `${Math.round(val * 3.28084)}ft`}
      />
      <div className={classes.range}>
        <span>{Math.round(15 * 3.28084)} ft</span>
        <span>{Math.round(1500 * 3.28084)} ft</span>
      </div>

      {/* Reposition controls */}
      <div className={classes.repositionRow}>
        {!repositioning ? (
          <>
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenWithIcon style={{ fontSize: 14 }} />}
              className={classes.repositionBtn}
              onClick={onStartReposition}
              disabled={disabled}
            >
              Reposition center
            </Button>
            {hasCustomCenter && (
              <Button
                size="small"
                className={classes.repositionBtn}
                onClick={onResetCenter}
                disabled={disabled}
                style={{ color: '#999' }}
              >
                Reset
              </Button>
            )}
          </>
        ) : (
          <>
            <Button
              size="small"
              variant="contained"
              color="primary"
              className={classes.repositionBtn}
              onClick={onConfirmReposition}
              style={{ background: '#1565c0' }}
            >
              Confirm
            </Button>
            <Button
              size="small"
              variant="outlined"
              className={classes.repositionBtn}
              onClick={onCancelReposition}
            >
              Cancel
            </Button>
          </>
        )}
      </div>

      <Typography className={classes.hint}>
        {repositioning
          ? 'Drag the blue dot on the map to move the geofence center.'
          : 'Drag slider to adjust radius — auto-saves on release.'}
      </Typography>

      {/* Explicit confirmation — only counts toward setup checklist once the
          user clicks Confirm. Keeps "Geofence set" from being green just
          because a default radius existed. */}
      <div
        className={`${classes.confirmRow} ${confirmed ? classes.confirmRowConfirmed : ''}`}
      >
        {confirmed ? (
          <>
            <CheckCircleIcon style={{ fontSize: 18, color: '#10B981' }} />
            <span className={`${classes.confirmText} ${classes.confirmTextDone}`}>
              Geofence confirmed
            </span>
            <Button
              size="small"
              className={classes.confirmBtn}
              onClick={onConfirm}
              disabled
              style={{ color: '#9CA3AF' }}
            >
              ✓
            </Button>
          </>
        ) : (
          <>
            <span className={classes.confirmText}>
              Ready? Click Confirm to mark the geofence as set.
            </span>
            <Button
              size="small"
              variant="contained"
              color="primary"
              className={classes.confirmBtn}
              onClick={onConfirm}
              disabled={disabled || repositioning || saving}
              style={{ background: '#1565c0' }}
            >
              Confirm
            </Button>
          </>
        )}
      </div>
    </Box>
  );
};

export default GeofenceSection;
